import { type FC, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { IconPollBgImg } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import BackgroundPicker, {
    DEFAULT_STORY_BACKGROUND,
    type StoryBackgroundKey,
    getStoryBackground
} from 'components/UI/BackgroundPicker/BackgroundPicker'
import Goal from 'components/UI/Goal/Goal'
import Sidebar from 'components/UI/Sidebar/Sidebar'
import SidebarItem from 'components/UI/Sidebar/SidebarItem'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useOrientation } from 'helpers/helperHooks'
import { getImageOrientation } from 'helpers/util'
import {
    attachPhotoFromS3,
    presignPhoto,
    uploadFileToS3
} from 'services/endpoints/attachment'
import { type IGoal } from 'types/interfaces/ITypes'

import './_addGoal.scss'

interface Props {
    handleClose: () => void
    handleSetGoalData: (data: IGoal) => void
    noControls?: boolean
    initialBackgroundImage?: string | null
    initialBackgroundFile?: File | null
    initialBackgroundUploadedSrc?: string | null
    initialBackgroundOrientation?: string
}

type GoalWithBgSrc = IGoal & {
    goalBgUploadedSrc?: string | null
}

const AddGoal: FC<Props> = ({
    handleClose,
    handleSetGoalData,
    noControls,
    initialBackgroundImage,
    initialBackgroundFile,
    initialBackgroundUploadedSrc,
    initialBackgroundOrientation
}) => {
    const [goalTitle, setGoalTitle] = useState('')
    const [goalAmount, setGoalAmount] = useState<string>('')

    const [bgColor, setBgColor] =
        useState<StoryBackgroundKey>(DEFAULT_STORY_BACKGROUND)

    const [bgImg, setBgImg] = useState<string | null>(
        initialBackgroundImage ?? null
    )
    const [bgImgFile, setBgImgFile] = useState<File | null>(
        initialBackgroundFile ?? null
    )
    const [bgImgOrientation, setBgImgOrientation] = useState<string>(
        initialBackgroundOrientation ?? ''
    )
    const [bgUploadedSrc, setBgUploadedSrc] = useState<string | null>(
        initialBackgroundUploadedSrc ?? null
    )
    const [bgUploading, setBgUploading] = useState(false)
    const [bgUploadError, setBgUploadError] = useState<string>('')

    const uploadTokenRef = useRef(0)

    const { setHideFooter } = useHeaderLayout()
    const [validationError, setValidationError] = useState('')

    const goalImgRef = useRef<HTMLInputElement>(null)
    const deviceOrientation = useOrientation()
    const { t } = useTranslation()

    useEffect(() => {
        setHideFooter(true)

        return () => {
            setHideFooter(false)
        }
    }, [setHideFooter])

    const resetBg = () => {
        setBgImg(null)
        setBgImgFile(null)
        setBgImgOrientation('')
        setBgUploadedSrc(null)
        setBgUploading(false)
        setBgUploadError('')
        uploadTokenRef.current += 1
    }

    const handleImgUpload = async (e: React.FormEvent<HTMLInputElement>) => {
        const input = e.currentTarget
        const file = input.files?.[0] ?? null

        if (!file) return

        input.value = ''

        const myToken = ++uploadTokenRef.current

        setBgUploadError('')
        setBgImgFile(file)
        setBgUploadedSrc(null)
        setBgUploading(true)

        const reader = new FileReader()

        reader.onload = async () => {
            const imgDataUrl = reader.result as string

            setBgImg(imgDataUrl)

            try {
                const orientation = await getImageOrientation(imgDataUrl)

                if (orientation) {
                    setBgImgOrientation(orientation)
                }
            } catch {
                // ignore orientation failure
            }
        }

        reader.readAsDataURL(file)

        try {
            const { url: putUrl, key } = await presignPhoto({
                type: 'post_image',
                contentType: file.type,
                size: file.size
            })
            if (uploadTokenRef.current !== myToken) return

            await uploadFileToS3(putUrl, file, () => {})

            if (uploadTokenRef.current !== myToken) return

            const attach = await attachPhotoFromS3({
                s3_key: key,
                type: 'post'
            })

            if (uploadTokenRef.current !== myToken) return

            const resData =
                (attach as any)?.data?.data ?? (attach as any)?.data ?? attach

            const src = resData?.storage_path ?? resData?.path ?? key

            setBgUploadedSrc(src)
            setBgUploading(false)
            setBgUploadError('')
        } catch (err: any) {
            console.error('[AddGoal] bg upload failed', err)

            if (uploadTokenRef.current !== myToken) return

            setBgUploading(false)
            setBgUploadedSrc(null)
            setBgUploadError(t('error:errorSomethingWentWrong'))
        }
    }

const validateGoal = (title: string, amount: number, cb: () => void) => {
    if (amount > 250) {
        setValidationError('Goal amount cannot exceed $250')

        setTimeout(() => {
            setValidationError('')
        }, 5000)

        return
    }

    if (!title?.trim().length || !amount) {
        if ((title || title.trim().length) && isNaN(amount)) {
            setValidationError('You must enter a valid amount')
        } else if (amount === 0) {
            setValidationError('You must enter a valid amount')
        } else {
            setValidationError(t('validation:allFieldsAreRequired'))
        }

        setTimeout(() => {
            setValidationError('')
        }, 5000)
    } else {
        cb()
    }
}

    const handleSaveGoal = () => {
        const hasSelectedImage = !!bgImgFile
        const imageReady =
            !hasSelectedImage ||
            (!!bgUploadedSrc && !bgUploading && !bgUploadError)

        if (!imageReady) {
            setValidationError(t('error:errorSomethingWentWrong'))

            setTimeout(() => {
                setValidationError('')
            }, 5000)

            return
        }

        validateGoal(goalTitle, parseFloat(goalAmount), () => {
            handleSetGoalData({
                title: goalTitle,
                amount: goalAmount,
                goalBg: bgImg ?? bgColor,
                goalBgImg: bgImgFile,
                goalBgUploadedSrc: bgUploadedSrc
            } satisfies GoalWithBgSrc)

            handleClose()
        })
    }

    if (!deviceOrientation) return null

    const hasImageSelected = !!bgImgFile
    const canSaveImageState =
        !hasImageSelected || (!!bgUploadedSrc && !bgUploading && !bgUploadError)

    const disableSave = bgUploading || !canSaveImageState

    return (
        <div
            className={classNames('addGoal', {
                'addGoal--withBgImg': !!bgImg,
                'device-portrait': deviceOrientation.type.includes('portrait'),
                'device-landscape': deviceOrientation.type.includes('landscape'),
                'img-portrait': bgImgOrientation === 'portrait',
                'img-landscape': bgImgOrientation === 'landscape'
            })}
            style={{
                backgroundImage: bgImg
                    ? `url(${bgImg})`
                    : getStoryBackground(bgColor)
            }}
        >
            {bgImg && (
                <div
                    className='addGoal__bg'
                    style={{ backgroundImage: `url(${bgImg})` }}
                />
            )}

            <div className='addGoal__wrapper'>
                <div className='addGoal__header'>
                    <div className='addGoal__header__title'>
                        {t('addGoal')}
                    </div>
                </div>

                {!noControls && (
                    <Sidebar>
                        <SidebarItem
                            icon={<IconPollBgImg />}
                            text={t('upload')}
                            onClick={() => {
                                goalImgRef.current?.click()
                            }}
                        >
                            <input
                                ref={goalImgRef}
                                type='file'
                                onChange={handleImgUpload}
                                id='goal__img__upload'
                                accept='image/*'
                                hidden
                            />
                        </SidebarItem>

                        {bgImg && (
                            <SidebarItem
                                icon={<IconPollBgImg />}
                                text='Remove'
                                onClick={resetBg}
                            />
                        )}

                        <BackgroundPicker
                            background={bgColor}
                            setBackground={(color: StoryBackgroundKey) => {
                                resetBg()
                                setBgColor(color)
                            }}
                        />
                    </Sidebar>
                )}

                <div className='addPoll__content'>
                    <Goal
                        title={goalTitle}
                        amount={goalAmount}
                        validationError={validationError}
                        handleTitleChange={(value: string) => {
                            setGoalTitle(value)
                        }}
handleAmountChange={(value: string) => {
    if (value === '') {
        setGoalAmount('')
        setValidationError('')
        return
    }

    const numericValue = Number(value)

    if (numericValue > 250) {
        setGoalAmount('250')
        setValidationError('Goal amount cannot exceed $250')
        return
    }

    setGoalAmount(value)
    setValidationError('')
}}
                    />
                </div>

                <div className='addPoll__footer'>
                    <Button
                        text={t('cancel')}
                        color='white'
                        font='mont-14-normal'
                        width='12'
                        height='3'
                        clickFn={handleClose}
                    />

                    <Button
                        text={t('save')}
                        color='black'
                        font='mont-14-normal'
                        width='13'
                        height='3'
                        clickFn={handleSaveGoal}
                        disabled={disableSave}
                    />
                </div>
            </div>
        </div>
    )
}

export default AddGoal